import axios, { AxiosInstance } from 'axios';
import { fetchAuthSession } from '@aws-amplify/auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(async (config) => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (error) {
    console.warn('Failed to get auth token:', error);
  }
  return config;
});

// Types
export interface User {
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  organizationId?: string;
  isPaid: boolean;
  isEnabled: boolean;
  status: string;
  createdAt: string;
  lastModified?: string;
  groups: string[];
  usage?: {
    period: string;
    submittedCount: number;
    completedCount: number;
    failedCount: number;
  };
}

export interface UserDetails extends User {
  status: string;
  maxRunsPerMonth?: number;
  maxDocChatLimit?: number;
  maxResearchLimit?: number;
  embeddingsEnabled: boolean;
  documentChatEnabled: boolean;
  vectorStoreId?: string;
  mfaEnabled: boolean;
}

export interface Job {
  jobId: string;
  userId: string;
  jobType: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  progress: number;
  currentStep?: string;
  outputLocation?: string;
  errorMessage?: string;
  processingTimeMs?: number;
  tokenUsage?: TokenUsage;
}

export interface Organization {
  id: string;
  name: string;
  displayName?: string;
  domain?: string;
  maxRunsPerMonth?: number;
  maxDocChatPerMonth?: number;
  maxResearchPerMonth?: number;
  features?: string[];
  enabledFeatures?: string[];
  status?: string;
  accountManagerName?: string;
  accountManagerEmail?: string;
  billingName?: string;
  billingAddress?: string;
  vatNumber?: string;
  costPerLicenseZAR?: number;
  sharedRunPool?: boolean;
}

export interface OrganizationInvoice {
  period: string;
  invoiceNumber: string;
  exists: boolean;
  url: string | null;
  generatedAt: string | null;
  fileSize: number | null;
}

export interface NdaRecord {
  filename: string;
  recipientName: string;
  generatedAt: string;
  fileSize: number;
  url: string;
}

export interface AgreementRecord {
  filename: string;
  customerName: string;
  generatedAt: string;
  fileSize: number;
  url: string;
}

export interface OverviewStats {
  period: string;
  generatedAt: string;
  totalUsers: number;
  totalOrganizations: number;
  jobsToday: number;
  jobsByStatus: Record<string, number>;
  topJobTypes: { type: string; count: number }[];
  activeProcessing: number;
  totalCostToday?: number;
  dailyCosts?: Array<{ date: string; cost: number }>;
  dailyCompletedJobs?: Array<{ date: string; count: number }>;
  recentJobs?: Array<{
    jobId: string;
    userId: string;
    userEmail?: string;
    jobType: string;
    status: string;
    createdAt: string;
    costUsd: number;
    processingTimeMs?: number;
    errorMessage?: string;
    inputTokens?: number;
    outputTokens?: number;
    outputLocation?: string;
  }>;
  totalRecentJobs?: number; // Total count for pagination
}

export interface TokenUsage {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  model?: string;
  costUsd?: number;
}

// Feature types
export enum Features {
  MEMO = 'MEMO',
  MERITS_ASSESSMENT = 'MERITS_ASSESSMENT',
  QUANTUM = 'QUANTUM',
  LOSS_ADJUSTER_REPORT = 'LOSS_ADJUSTER_REPORT',
  RECOVERY_MODEL = 'RECOVERY_MODEL',
  JUDGEMENT_SIMULATION = 'JUDGEMENT_SIMULATION',
  OMBUDSMAN_SIMULATION = 'OMBUDSMAN_SIMULATION',
  THIRD_PARTY_RESPONSE = 'THIRD_PARTY_RESPONSE',
  POLICY_RESPONSE = 'POLICY_RESPONSE',
  POLICY_ANALYSIS = 'POLICY_ANALYSIS',
  POLICY_WORDING_ANALYSIS = 'POLICY_WORDING_ANALYSIS',
  POLICY_WORDING_COMPARISON_COMMERCIAL = 'POLICY_WORDING_COMPARISON_COMMERCIAL',
  POLICY_WORDING_COMPARISON_PERSONAL = 'POLICY_WORDING_COMPARISON_PERSONAL',
  POLICY_CHANGE_COMPLIANCE_CHECKER = 'POLICY_CHANGE_COMPLIANCE_CHECKER',
  POLICY_QUOTE_COMPARISON = 'POLICY_QUOTE_COMPARISON',
  POLICY_QUOTE_COMPARISON_V2 = 'POLICY_QUOTE_COMPARISON_V2',
  EXPLANATION = 'EXPLANATION',
  EXPLANATION_COMPREHENSIVE = 'EXPLANATION_COMPREHENSIVE',
  EXPLANATION_COMPREHENSIVE_V2 = 'EXPLANATION_COMPREHENSIVE_V2',
  HPCSA_PI_RESPONSE = 'HPCSA_PI_RESPONSE',
  BROKER_RISK_ASSESSMENT = 'BROKER_RISK_ASSESSMENT',
  REPUDIATION_ASSESSMENT = 'REPUDIATION_ASSESSMENT',
  RECORD_OF_ADVICE_PERSONAL_STI = 'RECORD_OF_ADVICE_PERSONAL_STI',
  PERSONAL_ROA = 'PERSONAL_ROA',
  RECORD_OF_ADVICE_PERSONAL_STI_REPLACE = 'RECORD_OF_ADVICE_PERSONAL_STI_REPLACE',
  RECORD_OF_ADVICE_PERSONAL_RENEWAL = 'RECORD_OF_ADVICE_PERSONAL_RENEWAL',
  RECORD_OF_ADVICE_COMMERCIAL = 'RECORD_OF_ADVICE_COMMERCIAL',
  RECORD_OF_ADVICE_COMMERCIAL_V2 = 'RECORD_OF_ADVICE_COMMERCIAL_V2',
  RECORD_OF_ADVICE_COMMERCIAL_REPLACE = 'RECORD_OF_ADVICE_COMMERCIAL_REPLACE',
  RECORD_OF_ADVICE_COMMERCIAL_RENEWAL = 'RECORD_OF_ADVICE_COMMERCIAL_RENEWAL',
  RECORD_OF_ADVICE_LTI = 'RECORD_OF_ADVICE_LTI',
  RECORD_OF_ADVICE_REPLACE_LTI = 'RECORD_OF_ADVICE_REPLACE_LTI',
  GENESIS_PERSONAL_NEW = 'GENESIS_PERSONAL_NEW',
  GENESIS_COMMERCIAL_NEW = 'GENESIS_COMMERCIAL_NEW',
  GENESIS_COMMERCIAL_RENEWAL = 'GENESIS_COMMERCIAL_RENEWAL',
  GENESIS_COMMERCIAL_ENDORSEMENT = 'GENESIS_COMMERCIAL_ENDORSEMENT',
  TEXTBOOK_CHAT = 'TEXTBOOK_CHAT',
  GUIDES = 'GUIDES',
  EMAIL_INGESTION = 'EMAIL_INGESTION',
}

export enum FeatureVisibility {
  GENERAL = 'GENERAL',
  PROPRIETARY = 'PROPRIETARY',
}

export enum FeatureCategory {
  ASSESSMENT = 'ASSESSMENT',
  POLICY = 'POLICY',
  MEDICAL = 'MEDICAL',
  BROKER_PERSONAL = 'BROKER_PERSONAL',
  BROKER_COMMERCIAL = 'BROKER_COMMERCIAL',
  BROKER_LTI = 'BROKER_LTI',
  BROKER_TOOLS = 'BROKER_TOOLS',
  RESOURCES = 'RESOURCES',
}

export interface FeatureDefinition {
  id: Features;
  name: string;
  description: string;
  category: FeatureCategory;
  visibility: FeatureVisibility;
}

export type NotificationType = 'info' | 'success' | 'warning' | 'feature';
export type NotificationStatus = 'active' | 'inactive';

export interface Notification {
  id: string;
  title?: string;
  message: string;
  type: NotificationType;
  link?: string;
  linkText?: string;
  isModal: boolean;
  organizationIds?: string[];
  status: NotificationStatus;
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
  createdBy: string;
}

export interface FeaturesResponse {
  success: boolean;
  data: {
    features: FeatureDefinition[];
    byCategory: Record<FeatureCategory, FeatureDefinition[]>;
    byVisibility: {
      general: FeatureDefinition[];
      proprietary: FeatureDefinition[];
    };
    categoryLabels: Record<FeatureCategory, string>;
    summary: {
      total: number;
      general: number;
      proprietary: number;
    };
  };
}

// Admin API functions
export const adminApi = {
  // Users
  listUsers: async (params?: { 
    limit?: number; 
    paginationToken?: string; 
    group?: string;
    includeUsage?: boolean;
  }) => {
    const response = await api.get('/admin/users', { params });
    return response.data;
  },

  getUser: async (userId: string) => {
    const response = await api.get(`/admin/users/${userId}`);
    return response.data;
  },

  getUserJobs: async (userId: string, params?: { 
    limit?: number; 
    startKey?: string;
    status?: string;
    jobType?: string;
  }) => {
    const response = await api.get(`/admin/users/${userId}/jobs`, { params });
    return response.data;
  },

  exportUserJobs: async (userId: string, params: {
    months?: number;
    format?: 'pdf' | 'excel';
  }) => {
    const response = await api.post(`/admin/users/${userId}/export`, params);
    return response.data;
  },

  createUser: async (userData: {
    email: string;
    firstName?: string;
    lastName?: string;
    organizationId?: string;
    isPaid?: boolean;
    maxRunsPerMonth?: number;
    groups?: string[];
    sendInvitation?: boolean;
  }) => {
    const response = await api.post('/admin/users', userData);
    return response.data;
  },

  disableUser: async (userId: string) => {
    const response = await api.post(`/admin/users/${userId}/disable`);
    return response.data;
  },

  enableUser: async (userId: string) => {
    const response = await api.post(`/admin/users/${userId}/enable`);
    return response.data;
  },

  deleteUser: async (userId: string) => {
    const response = await api.post(`/admin/users/${userId}/delete`);
    return response.data;
  },

  resendUserInvitation: async (userId: string) => {
    const response = await api.post(`/admin/users/${userId}/resend-invitation`);
    return response.data;
  },

  resendUserVerification: async (userId: string) => {
    const response = await api.post(`/admin/users/${userId}/resend-verification`);
    return response.data;
  },

  updateUser: async (userId: string, updates: {
    organizationId?: string;
    isPaid?: boolean;
    maxRunsPerMonth?: number;
    maxDocChatLimit?: number;
    maxResearchLimit?: number;
    firstName?: string;
    lastName?: string;
    embeddings?: boolean;
    documentChat?: boolean;
  }) => {
    const response = await api.put(`/admin/users/${userId}`, updates);
    return response.data;
  },

  // Organizations
  listOrganizations: async () => {
    const response = await api.get('/admin/organizations');
    return response.data;
  },

  getOrganization: async (orgId: string) => {
    const response = await api.get(`/admin/organizations/${orgId}`);
    return response.data;
  },

  createOrganization: async (orgData: {
    name: string;
    displayName?: string;
    domain?: string;
    maxRunsPerMonth?: number;
    sharedRunPool?: boolean;
    features?: string[];
    accountManagerName?: string;
    accountManagerEmail?: string;
  }) => {
    const response = await api.post('/admin/organizations', orgData);
    return response.data;
  },

  updateOrganization: async (orgId: string, updates: {
    displayName?: string;
    domain?: string;
    maxRunsPerMonth?: number;
    maxDocChatPerMonth?: number;
    maxResearchPerMonth?: number;
    sharedRunPool?: boolean;
    enabledFeatures?: string[];
    status?: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
    accountManagerName?: string;
    accountManagerEmail?: string;
    billingName?: string;
    billingAddress?: string;
    vatNumber?: string;
    costPerLicenseZAR?: number;
  }) => {
    const response = await api.put(`/admin/organizations/${orgId}`, updates);
    return response.data;
  },

  deleteOrganization: async (orgId: string) => {
    const response = await api.delete(`/admin/organizations/${orgId}`);
    return response.data;
  },

  listOrganizationMembers: async (orgId: string, params?: {
    limit?: number;
    lastKey?: string;  // Base64 encoded DynamoDB pagination key
    includeUsage?: boolean;
  }) => {
    const response = await api.get(`/admin/organizations/${orgId}/members`, { params });
    return response.data;
  },

  // Features
  listFeatures: async (): Promise<FeaturesResponse> => {
    const response = await api.get('/admin/features');
    return response.data;
  },

  bulkUpdateFeature: async (params: {
    featureId: string;
    action: 'enable' | 'disable';
    organizationIds: string[] | ['all'];
  }) => {
    const response = await api.post('/admin/features/bulk-update', params);
    return response.data;
  },

  // Export
  exportUsersExcel: async (params?: {
    period?: string;
    organizationId?: string;
    includeDisabled?: boolean;
  }) => {
    const response = await api.post('/admin/export/users/excel', params);
    return response.data;
  },

  exportUsersPdf: async (params?: {
    period?: string;
    organizationId?: string;
    includeDisabled?: boolean;
    reportTitle?: string;
  }) => {
    const response = await api.post('/admin/export/users/pdf', params);
    return response.data;
  },

  // Dashboard
  getOverviewStats: async (params?: {
    recentJobsOffset?: number;
    recentJobsLimit?: number;
  }): Promise<{ success: boolean; data: OverviewStats }> => {
    const response = await api.get('/admin/stats/overview', { params });
    return response.data;
  },

  /** Broadcast HTML email to Cognito users (SES). */
  broadcastEmail: async (params: {
    subject: string;
    bodyHtml: string;
    onlyPaid?: boolean;
    onlyEnabled?: boolean;
  }): Promise<{
    success: boolean;
    data: { message: string; recipientCount: number; sent: number; failed: number };
  }> => {
    const response = await api.post('/admin/broadcast-email', params);
    return response.data;
  },

  // Job Status (for async exports)
  getExportJobStatus: async (jobId: string) => {
    const response = await api.get(`/admin/jobs/${jobId}`);
    return response.data;
  },

  // Organization Invoices
  listOrganizationInvoices: async (orgId: string) => {
    const response = await api.get(`/admin/organizations/${orgId}/invoices`);
    return response.data;
  },

  generateOrganizationInvoice: async (orgId: string, period: string, customData?: {
    customerName?: string;
    customerAddress?: string;
    vatNumber?: string;
    description?: string;
    numLicenses?: number;
    amountZar?: number;
  }) => {
    const response = await api.post(
      `/admin/organizations/${orgId}/invoices/${period}/generate`,
      customData || {}
    );
    return response.data;
  },

  listOrganizationJobExports: async (orgId: string) => {
    const response = await api.get(`/admin/organizations/${orgId}/job-exports`);
    return response.data;
  },

  exportOrganizationJobs: async (orgId: string, period: string) => {
    const response = await api.post('/admin/export-organization-jobs', {
      orgId,
      period
    });
    return response.data;
  },

  // Notifications
  listNotifications: async () => {
    const response = await api.get('/admin/notifications');
    return response.data;
  },

  createNotification: async (data: {
    title?: string;
    message: string;
    type: NotificationType;
    link?: string;
    linkText?: string;
    isModal?: boolean;
    organizationIds?: string[];
    expiresAt: string;
  }) => {
    const response = await api.post('/admin/notifications', data);
    return response.data;
  },

  updateNotification: async (id: string, updates: {
    title?: string;
    message?: string;
    type?: NotificationType;
    link?: string;
    linkText?: string;
    isModal?: boolean;
    organizationIds?: string[];
    expiresAt?: string;
    status?: NotificationStatus;
  }) => {
    const response = await api.put(`/admin/notifications/${id}`, updates);
    return response.data;
  },

  deleteNotification: async (id: string) => {
    const response = await api.delete(`/admin/notifications/${id}`);
    return response.data;
  },

  // NDAs
  listNdas: async () => {
    const response = await api.get('/admin/ndas');
    return response.data;
  },

  generateNda: async (data: {
    recipientName: string;
    recipientAddress: string;
    startDate: string;
  }) => {
    const response = await api.post('/admin/ndas/generate', data);
    return response.data;
  },

  // Agreements
  listAgreements: async () => {
    const response = await api.get('/admin/agreements');
    return response.data;
  },

  generateAgreement: async (data: {
    customerName: string;
    customerAddress: string;
    effectiveDate: string;
    monthlyFee: string;
    numLicenses: number;
    numRuns: number;
    duration: number;
  }) => {
    const response = await api.post('/admin/agreements/generate', data);
    return response.data;
  },
};

export default api;

